/**
 * AdminDocumentsPage — operational documents store (Sprint Documents v1).
 *
 * Org-scoped admin view to upload / list / download / delete operational
 * PDFs/images/office docs (contracts, SOPs, owner agreements, etc.).
 *
 * Responsive shape per RESPONSIVE_PATTERNS.md (M2):
 *   Pattern 2 — table (md:+) ↔ card stack (< md:)
 *   Pattern 5 — filter inputs `w-full sm:w-[220px]`
 *
 * Upload modal uses Radix Dialog directly (no ResponsiveModal — that's
 * M3+ migration scope per the v1 spec).
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Folder, Folders, Search } from "lucide-react";
import { Link } from "react-router-dom";
import {
  fetchAdminOperationalDocuments,
  fetchAdminOperationalDocumentSummary,
  fetchAdminOperationalDocumentDownloadUrl,
  deleteAdminOperationalDocument,
} from "../../api/adminData";
import BexioTablePagination from "../../components/admin/bexio/workspace/BexioTablePagination";
import { useDebouncedValue } from "../../components/admin/bexio/workspace/useDebouncedValue";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatDateOnly } from "../../utils/datetime";
import { documentTypeFor, formatBytes } from "./documents/documentDisplay";
import DocumentRowActions from "./documents/DocumentRowActions";
import DocumentCategoryCards from "./documents/DocumentCategoryCards";
import { categoryAccent } from "./documents/documentCategoryAccent";
import DocumentUploadDialog from "./documents/DocumentUploadDialog";


/**
 * Authoritative whitelist — must match backend DocumentCategory Literal.
 *
 * THE ORDER IS THE FOLDER ORDER. This array is read three times: the folder
 * rail, the compact folder select below `lg:`, and the upload form. One array
 * means a reader who learns where "Rechnungen" sits finds it in the same place
 * every time, and a category can never exist in one of the three and not the
 * others. The three categories added in S4 are inserted where they belong
 * rather than appended, so nothing that already had a position moved.
 */
const CATEGORY_OPTIONS = [
  // DISPLAY ONLY. The stored key stays `mietvertraege`; what changed is
  // what the folder is called, because the folder has always also held
  // Kündigungsschreiben and Kündigungsbestätigungen and the old name
  // did not say so. No row, no request and no whitelist moves for a
  // rename that is purely a caption.
  { value: "mietvertraege", label: "Mietverträge & Kündigungen" },
  { value: "bewerbungs_dokumente", label: "Bewerbungsdokumente" },
  { value: "hausordnung_sops", label: "Hausordnung & SOPs" },
  { value: "eigentuemer_vertraege", label: "Eigentümer-Verträge" },
  { value: "rechnungen", label: "Rechnungen" },
  { value: "interne_dokumente", label: "Interne Dokumente" },
  { value: "marketing_kommunikation", label: "Marketing & Kommunikation" },
  { value: "behoerden_recht", label: "Behörden & Recht" },
  { value: "ai_cto_reports", label: "AI / CTO Reports" },
  { value: "konkurrenzanalysen", label: "Konkurrenzanalysen" },
  { value: "sonstiges", label: "Sonstiges" },
];

const CATEGORY_LABEL_BY_VALUE = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label])
);

/**
 * One page of documents.
 *
 * 25 is what the repo's other STANDALONE admin lists page at — the invoice
 * dashboard uses it on both halves (`routes_admin_invoice_dashboard`'s
 * `limit` default and `useInvoiceDashboardData.DEFAULT_LIMIT`) — and it is
 * the middle step of `BexioTablePagination`'s own 10/25/50. The 10 used by
 * `TenantInvoicesTab` belongs to a tab inside a detail page, which is a
 * smaller surface than a full list route.
 *
 * Before this the page asked for 200 rows and rendered every one, so an org
 * with 201 documents silently never saw the 201st: no control said there
 * was more, because `total` was fetched and thrown away.
 */
const PAGE_SIZE = 25;

/**
 * The six orderings the toolbar offers, as `${sort}:${direction}`.
 *
 * One `<select>` rather than two because the pair is one decision to the
 * reader — "Titel A–Z" is a thing, "title" plus "asc" is not. The value is
 * split on the colon and the halves go on the wire as the two query
 * parameters the route declares; the labels never travel.
 */
const SORT_OPTIONS = [
  { value: "uploaded_at:desc", label: "Neueste zuerst" },
  { value: "uploaded_at:asc", label: "Älteste zuerst" },
  { value: "title:asc", label: "Titel A–Z" },
  { value: "title:desc", label: "Titel Z–A" },
  { value: "file_size_bytes:desc", label: "Grösse absteigend" },
  { value: "file_size_bytes:asc", label: "Grösse aufsteigend" },
];

/** Newest first — the ordering the list had before it had any choice. */
const DEFAULT_SORT_VALUE = "uploaded_at:desc";

/**
 * The category, as a quiet neutral pill.
 *
 * S5 took the shout out of it — it was uppercase, bold and letter-spaced,
 * which put it in direct competition with the title beside it for the eye
 * of someone scanning the list. The category is context, not identity, so
 * it now reads at the weight of context. Same tokens, same text, same
 * fallback: a value outside the whitelist still renders as itself.
 *
 * Deliberately NOT eleven colours. Colour-coding the taxonomy is a design
 * decision with an accessibility bill attached (§18: never information by
 * colour alone), and it is not this slice's.
 */
function CategoryPill({ value }) {
  const label = CATEGORY_LABEL_BY_VALUE[value] || value;
  const accent = categoryAccent(value);
  return (
    // CHANGED FROM S5's deliberately neutral pill. S5 wrote "deliberately NOT
    // eleven colours … the accessibility bill is not this slice's"; the bill
    // is paid in `documentCategoryAccent.js` — the label is always present in
    // full, so the hue is a second channel and never the only one. The
    // SURFACE stays a token (theme guard T6); only the text and the hairline
    // carry the accent, each with its `dark:` twin.
    <span
      className={`inline-flex shrink-0 items-center rounded-md border bg-muted px-2 py-0.5 text-[11px] font-medium ${accent.border} ${accent.icon}`}
    >
      {label}
    </span>
  );
}


/**
 * The identity of one document: what kind of file it is, and what it is called.
 *
 * ONE COMPONENT FOR BOTH VIEWPORTS. The table and the card stack are two
 * renderings of the same rows, and before S5 they disagreed about what a
 * document's identity was — the card showed `related_to_label` as an
 * unlabelled paragraph under the title while the table gave it a column of
 * its own. Sharing this block is what keeps the two from drifting again.
 *
 * THE TITLE IS CLAMPED, NEVER CUT. `line-clamp-2` is CSS: the full string
 * stays in the DOM and in the accessibility tree, and `title` puts it in
 * reach of a pointer. S1 pinned "renders in full, untruncated" against a
 * cell that simply grew — a 200-character filename used to push the other
 * five columns off the screen. Nothing is lost here; only the height is
 * bounded. A Radix tooltip would be the richer answer, but `ui/tooltip.jsx`
 * is an unused scaffold with no provider mounted anywhere in the app, and
 * introducing one is a new interaction pattern rather than this slice's job.
 *
 * THE ICON IS DECORATIVE. It repeats the label rendered right beside it, so
 * it is `aria-hidden` and a screen reader hears "PDF" once rather than
 * twice (§18).
 */
function DocumentIdentity({ doc, className = "" }) {
  const { Icon, label, code } = documentTypeFor(doc.mime_type);
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      {/* Icon AND format code, because at a glance a badge reading "XLS" is
          faster to tell from "PDF" than two line-art glyphs are. Still one
          decision: both come from the same `documentTypeFor` group, and the
          whole badge stays `aria-hidden` — the label below it already says
          "Tabelle" in words, and a reader should hear that once. */}
      <span
        className="mt-0.5 flex h-8 w-8 shrink-0 flex-col items-center justify-center gap-px rounded-[8px] border border-border bg-muted text-muted-foreground"
        aria-hidden="true"
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[8px] font-bold leading-none tracking-tight">
          {code}
        </span>
      </span>
      {/* THE CAP IS LOAD-BEARING, not decoration. `break-words` only breaks
          an over-long token when the line box has somewhere to break TO, and
          `line-clamp-2` only clamps against a definite width. A table cell in
          an auto-layout table has neither: it grows to fit its content, so
          `Mietvertrag_sehr_lang_sehr_lang_…` — one unbroken token — widened
          the column until the other five were pushed out of the viewport.
          The `w-[38%]` on the header is a hint the browser is free to
          override; this is not. On the card it never binds, because the card
          is narrower than the cap anyway.

          IT TIGHTENS WHERE SPACE IS SCARCE. At `lg:` the folder rail already
          takes 280px, so the table has roughly 680px for six columns and two
          action buttons — it does not fit, and has scrolled inside its own
          `overflow-x-auto` since long before S5. A narrow cap there keeps the
          identity column from making that worse; from `xl:` up there is
          more, but not unlimited — at 420px a long title still pushed the
          "Löschen" button behind the table's scroller at 1440, and an
          action you have to scroll to find is a broken action. 300px keeps
          every column reachable at 1440 and still shows some 60 characters
          across two lines; the rest is one hover away. */}
      <div className="min-w-0 max-w-[220px] xl:max-w-[300px]">
        <p
          className="m-0 line-clamp-2 break-words text-[13px] font-semibold leading-snug text-foreground"
          title={doc.title}
        >
          {doc.title}
        </p>
        <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}


/**
 * The folder rail: one entry per category, each stating its stock.
 *
 * THERE IS NO FOLDER TABLE BEHIND THIS. A folder IS a category — the same
 * `category` filter the list endpoint has always taken, given a shape a
 * reader can navigate instead of a dropdown they have to open. Nothing here
 * nests, nothing is draggable, and no row is a directory: inventing a file
 * system would promise moves and hierarchies the data model does not have.
 *
 * COUNTS ARE STOCK, NOT RESULTS. Each number is how many documents the
 * category holds in this organisation, unaffected by the search box, the
 * sort order or which page is open. That is what makes them worth reading:
 * a number that shrank while you typed would answer a question nobody asked
 * and would have to be re-fetched on every keystroke to answer even that.
 *
 * `countsReady` is false while the summary is in flight AND if it failed —
 * both mean "not known", and an em dash says so. A folder never renders a
 * stale or invented number, and a failed summary costs the reader the
 * counts and nothing else: every folder still navigates.
 */
function DocumentFolderNav({ activeCategory, onSelect, total, byCategory, countsReady }) {
  const entries = [
    // "" is the all-documents filter the page has always used for "no
    // category", so the rail introduces no second vocabulary for it.
    { value: "", label: "Alle Dokumente", count: total, Icon: Folders },
    ...CATEGORY_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label,
      // A known folder with nothing in it reads 0, not blank: the backend
      // seeds every whitelisted key, and `?? 0` covers a payload that
      // predates a category being added.
      count: byCategory[o.value] ?? 0,
      Icon: Folder,
    })),
  ];

  return (
    <nav
      aria-label="Ordner"
      className="rounded-[12px] border border-border bg-card p-3"
    >
      <p className="mb-2 px-1 text-[12px] font-semibold text-foreground">
        Ordner
      </p>
      <ul className="m-0 list-none space-y-0.5 p-0">
        {entries.map(({ value, label, count, Icon }) => {
          const active = activeCategory === value;
          return (
            <li key={value || "__all__"}>
              <button
                type="button"
                onClick={() => onSelect(value)}
                // `aria-current` rather than colour alone: the active folder
                // has to be announced, not just seen.
                aria-current={active ? "true" : undefined}
                className={`flex w-full items-start gap-2 rounded-[8px] border px-2.5 py-2 text-left text-[13px] transition-colors ${
                  active
                    ? "border-primary bg-primary/10 font-semibold text-foreground"
                    : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {/* The folder's own accent, so the rail and the cards
                    above it identify a folder the same way. Colour is never
                    the only channel: the name is right beside it. */}
                <Icon
                  className={`mt-[2px] h-4 w-4 shrink-0 ${
                    active ? "text-primary" : categoryAccent(value).icon
                  }`}
                  aria-hidden="true"
                />
                {/* Wraps rather than truncates. The folder name IS the
                    navigation target, so "Marketing & Kommunikation" taking
                    two lines beats an ellipsis that hides which folder it is. */}
                <span className="min-w-0 flex-1 leading-snug">{label}</span>
                <span
                  className={`shrink-0 tabular-nums text-[12px] ${
                    active ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {countsReady ? count : "—"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}


export default function AdminDocumentsPage() {
  // The toolbar's three visible labels had no `htmlFor`, so each control
  // reached assistive tech unnamed while a caption floated beside it — the
  // same defect S6 closed inside the upload dialog, still open out here.
  // `useId` rather than module constants: two instances of this page on one
  // screen would otherwise share ids and a label would name the wrong input.
  const fieldId = useId();
  const searchId = `${fieldId}-suche`;
  const folderId = `${fieldId}-ordner`;
  const sortId = `${fieldId}-sortierung`;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [sortValue, setSortValue] = useState(DEFAULT_SORT_VALUE);
  const [offset, setOffset] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  // Folder stock, fetched separately from the list and on a different
  // cadence (see `loadCounts`). `null` means "not known yet", which the rail
  // renders as an em dash; an empty object would instead be a claim that
  // every folder is empty, and that claim would be wrong exactly while the
  // summary is in flight.
  const [counts, setCounts] = useState(null);
  const [countsError, setCountsError] = useState("");

  // The box stays fully responsive; only the request waits. Same hook and
  // the same 250 ms the Bexio workspace and the invoice dashboard use —
  // this page previously fired one scan per keystroke, so "Mietvertrag"
  // cost eleven requests of which ten were already obsolete on arrival.
  const debouncedSearch = useDebouncedValue(search);
  const [sort, direction] = useMemo(() => sortValue.split(":"), [sortValue]);

  /**
   * Everything that changes WHICH documents are being listed, as opposed to
   * which page of them is shown. Any change here puts the reader back on the
   * first page — page four of a different result set is not a place.
   *
   * THE RESET HAPPENS DURING RENDER, not in an effect. An effect would let
   * one request go out with the new filter and the old offset before the
   * reset landed, so every filter change would cost two requests and the
   * first would be thrown away. Adjusting state while rendering is React's
   * own answer to this; `useInvoiceDashboardData` does exactly the same.
   */
  const populationKey = JSON.stringify({ category, debouncedSearch, sortValue });
  const [seenPopulation, setSeenPopulation] = useState(populationKey);
  if (seenPopulation !== populationKey) {
    setSeenPopulation(populationKey);
    setOffset(0);
  }
  // Within this render the reset has not been applied yet, so the loader is
  // told the offset that will actually be in force.
  const effectiveOffset = seenPopulation === populationKey ? offset : 0;

  /**
   * Monotonic request generation. Type "Vert" then "Vertrag" and the first
   * answer can land after the second; without this the older, wider result
   * set would overwrite the newer one and the page would contradict its own
   * search box. Only the newest request may write state — and that holds for
   * its FAILURE too, so a stale error cannot blank a current success.
   */
  const generation = useRef(0);

  const load = useCallback(() => {
    const mine = ++generation.current;
    setLoading(true);
    setError("");
    return fetchAdminOperationalDocuments({
      category: category || undefined,
      search: debouncedSearch || undefined,
      sort,
      direction,
      limit: PAGE_SIZE,
      offset: effectiveOffset,
    })
      .then((data) => {
        if (mine !== generation.current) return; // superseded
        const nextItems = Array.isArray(data?.items) ? data.items : [];
        const nextTotal = typeof data?.total === "number" ? data.total : 0;
        setItems(nextItems);
        setTotal(nextTotal);

        // PAST-END SELF-HEAL.
        //
        // The population can shrink under our feet: a second admin — or this
        // admin's second tab — deletes rows while this client sits on page
        // four. The next read then asks for offset 75 of a set that now has
        // 26 rows, and the server answers honestly with no rows at all. That
        // is not an empty organisation and must not be presented as one: the
        // reader would lose the list AND the controls to get back to it,
        // because the pagination only renders when there are rows.
        //
        // So the newest response repairs its own offset. `lastValid` is the
        // first index of the last page that still exists, and the guard is
        // `> lastValid` rather than `>= nextTotal` because it states exactly
        // what is wrong — we are past the end — and because it makes the step
        // STRICTLY decreasing, so the heal cannot ping-pong or loop even if a
        // server answered inconsistently. At offset 0 nothing is past the end,
        // so a genuinely empty result is left alone (see `empty` below).
        //
        // This sits INSIDE the generation guard on purpose. A stale past-end
        // answer arriving after a newer good one must not move the offset —
        // it would drag the reader off the page they are already looking at.
        const lastValid =
          nextTotal > 0
            ? Math.floor((nextTotal - 1) / PAGE_SIZE) * PAGE_SIZE
            : 0;
        if (nextItems.length === 0 && nextTotal > 0 && effectiveOffset > lastValid) {
          // One extra read, and only in this genuinely concurrent case. The
          // offset change is what re-runs it, exactly as in the delete
          // step-back — never `setOffset` AND `load()`.
          setOffset(lastValid);
        }
      })
      .catch((e) => {
        if (mine !== generation.current) return; // superseded
        setError(e?.message || "Dokumente konnten nicht geladen werden.");
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (mine === generation.current) setLoading(false);
      });
  }, [category, debouncedSearch, sort, direction, effectiveOffset]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Counts get their OWN generation counter, separate from the list's.
   *
   * Sharing one would let a list response invalidate an in-flight summary
   * and vice versa, which is not what either guard is for: each protects
   * one resource against an older version of itself. Two deletes in quick
   * succession are the real case — the first summary can land after the
   * second and would otherwise put the pre-delete number back under the
   * rail while the list beside it already shows the truth.
   */
  const countsGeneration = useRef(0);

  /**
   * Fetch folder stock.
   *
   * DEPENDENCY-FREE ON PURPOSE. It reads no filter, no sort and no offset,
   * so it never needs re-creating — which is precisely what keeps it off
   * the search, folder, sort and pagination paths. A `loadCounts` that
   * closed over `category` would be re-created on every folder click and
   * any effect depending on it would fire a summary request nobody asked
   * for. The counts move when the STOCK moves: mount, upload, delete.
   *
   * ITS FAILURE IS ITS OWN. The message goes to `countsError`, never to
   * the page's `error`. Counts are navigation metadata; losing them must
   * not take down the document list, which is the thing the reader came
   * for. The rail keeps navigating, with em dashes where numbers were.
   */
  const loadCounts = useCallback(() => {
    const mine = ++countsGeneration.current;
    return fetchAdminOperationalDocumentSummary()
      .then((data) => {
        if (mine !== countsGeneration.current) return; // superseded
        setCounts({
          total: typeof data?.total === "number" ? data.total : 0,
          category_counts:
            data && typeof data.category_counts === "object" && data.category_counts
              ? data.category_counts
              : {},
        });
        setCountsError("");
      })
      .catch((e) => {
        if (mine !== countsGeneration.current) return; // superseded
        setCounts(null);
        setCountsError(
          e?.message || "Ordnerzahlen konnten nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  const countsReady = counts !== null;
  const folderTotal = counts?.total ?? 0;
  const folderByCategory = counts?.category_counts ?? {};

  function handleResetFilters() {
    setSearch("");
    setCategory("");
  }

  async function handleDownload(doc) {
    try {
      const { url } = await fetchAdminOperationalDocumentDownloadUrl(doc.id);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Download-URL fehlt.");
      }
    } catch (err) {
      toast.error(err?.message || "Download fehlgeschlagen.");
    }
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteAdminOperationalDocument(pendingDelete.id);
      toast.success("Dokument gelöscht.");
      setPendingDelete(null);
      // Deleting the last row of the last page leaves an offset that no
      // longer addresses anything, and refetching it would render an empty
      // page under a footer still claiming there are results. Step back one
      // page instead — and do it INSTEAD OF calling load(), not as well as:
      // changing the offset is itself what re-runs the read, so either
      // branch costs exactly one refetch.
      const remaining = Math.max(0, total - 1);
      if (effectiveOffset > 0 && effectiveOffset >= remaining) {
        setOffset(Math.max(0, effectiveOffset - PAGE_SIZE));
      } else {
        void load();
      }
      // The stock shrank in BOTH branches, so the summary is re-read in
      // both — one list read plus one counts read, never two of either.
      // A failed delete reaches neither: it throws straight to the catch.
      void loadCounts();
    } catch (err) {
      toast.error(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * An empty PAGE is not an empty POPULATION.
   *
   * `total === 0` is the whole condition, and it is what keeps the first-run
   * copy honest while a past-end offset heals itself: between the empty
   * answer for offset 75 and the good answer for offset 25 there is a moment
   * with no rows on screen, and without this an organisation holding 26
   * documents would be told "Noch keine Dokumente. Erstes Dokument
   * hochladen?". Reading `total` rather than a transient healing flag means
   * the copy cannot flash no matter how React interleaves the response
   * batch, the `finally` and the effect that fires the repair read.
   */
  const empty = !loading && items.length === 0 && !error && total === 0;
  const hasFilters = !!search || !!category;

  /**
   * "Lade Dokumente …" is for the FIRST load only.
   *
   * Every refetch used to unmount the whole list and put this line in its
   * place, so paging, sorting or typing made the page blink from content to
   * one grey sentence and back. Rows now stay put while the next page is in
   * flight; the pagination footer takes `busy` and dims its own controls,
   * which is the only movement a refetch should cause.
   */
  const initialLoading = loading && items.length === 0 && !error;

  const headerBlock = (
    <>
      {/* The repo's breadcrumb shape, not a new one: a plain `<nav
          aria-label="Breadcrumb">` with `/` separators, as
          AdminResponsibilityAreasPage.jsx:182 writes it. "Betrieb" is the
          section this page already lives under in the admin sidebar, so the
          trail names where the reader is rather than inventing a hierarchy
          the navigation does not have. */}
      <nav
        aria-label="Breadcrumb"
        className="mb-4 text-[11px] text-muted-foreground"
      >
        <Link
          to="/admin"
          className="rounded-[4px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Start
        </Link>
        <span className="mx-2" aria-hidden="true">
          /
        </span>
        <span className="text-muted-foreground">Betrieb</span>
        <span className="mx-2" aria-hidden="true">
          /
        </span>
        <span aria-current="page" className="text-foreground">
          Dokumente
        </span>
      </nav>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {/* Decorative: it repeats the heading beside it, so a screen
              reader should not announce "Dokument, Dokumente". */}
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] border border-border bg-card text-primary"
            aria-hidden="true"
          >
            <FileText className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="m-0 text-[24px] font-bold tracking-tight text-foreground">
              Dokumente
            </h1>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Interne Dokumente, Vorlagen und wichtige Unterlagen für die ganze
              Organisation.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-[10px] bg-primary px-4 py-2.5 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          + Dokument hochladen
        </button>
      </header>
    </>
  );

  const filterBar = (
    <div className="mb-4 flex flex-wrap items-center gap-2.5">
      <div className="min-w-[200px] flex-1">
        <label htmlFor={searchId} className="sr-only">
          Suche (Titel)
        </label>
        {/* The magnifier is decoration on a field that already says
            "Dokumente suchen …"; the label above is what names it. */}
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id={searchId}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Dokumente suchen …"
            className="block h-10 w-full rounded-[10px] border border-input bg-background pl-9 pr-3 text-[13px] text-foreground outline-none transition-colors hover:border-primary/40 focus:border-ring focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      {/* The compact folder control, and ONLY below `xl:`. Above it the
          rail is the folder navigation and a second control for the same
          truth would be two places to look and two places to be wrong.
          Below it there is no room for a rail, and this is the repo's
          Pattern 5 filter control — already accessible, already themed,
          and cheaper than an eleven-item horizontal scroller. It speaks
          the rail's vocabulary ("Ordner", "Alle Dokumente") and writes the
          SAME `category` state, so the two viewports cannot disagree.

          The breakpoint here is the exact complement of the rail's below,
          and that is load-bearing: one `xl:` moved without the other would
          leave either no folder control at all between 1024 and 1280, or
          two of them for the same state.

          No counts in the options: an option label that changed as
          documents arrived would be a moving target for anything reading
          this select, and the number the reader needs on a phone — how
          many are in the folder they are standing in — is already in the
          pagination footer below the list. */}
      <div className="xl:hidden">
        <label
          htmlFor={folderId}
          className="sr-only"
        >
          Ordner
        </label>
        <select
          id={folderId}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="block h-10 w-full rounded-[10px] border border-input bg-background px-3 text-[13px] text-foreground outline-none transition-colors hover:border-primary/40 focus:border-ring focus-visible:ring-2 focus-visible:ring-ring sm:w-[210px]"
        >
          <option value="">Alle Dokumente</option>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor={sortId}
          className="sr-only"
        >
          Sortierung
        </label>
        <select
          id={sortId}
          value={sortValue}
          onChange={(e) => setSortValue(e.target.value)}
          className="block h-10 w-full rounded-[10px] border border-input bg-background px-3 text-[13px] text-foreground outline-none transition-colors hover:border-primary/40 focus:border-ring focus-visible:ring-2 focus-visible:ring-ring sm:w-[210px]"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {hasFilters ? (
        <button
          type="button"
          onClick={handleResetFilters}
          className="h-10 shrink-0 rounded-[10px] border border-border bg-card px-3.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          Zurücksetzen
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="mx-auto max-w-[1400px]">
      {headerBlock}

      {/* Rail + list. `minmax(0,1fr)` rather than a bare `1fr` on the second
          track: a grid track sized `1fr` still has `min-width: auto`, so the
          table's own horizontal scroller would push the track wider than the
          page instead of scrolling inside it, and the whole document would
          gain a horizontal scrollbar.

          `xl:` is where the rail appears, and S4's `lg:` was measured wrong.
          RESPONSIVE_PATTERNS.md's locked `lg:` decision is about the GLOBAL
          nav sidebar in AdminLayout — the one that lives in a Vaul drawer
          below `lg:`. This is a SECOND, page-internal rail sitting beside
          that one, and the doc says nothing about it. Repo precedent for a
          page-internal second column starting later exists:
          AdminAutomationsPage uses `xl:`, AdminInvoiceDashboardPage `2xl:`.

          The numbers, measured in the real shell rather than in isolation
          (the S4/S6 measurements were taken without AdminLayout and so
          understated every figure). At 1024 the global sidebar takes 288px
          and the layout padding 80px, leaving 656px of content. A 240px
          rail out of that left 358px for a table whose minimum is 825px —
          467px of internal scroll, i.e. the Aktionen column off-screen on
          every row. Moving the rail to `xl:` returns those 240px and cuts
          it to 171px.

          What this does NOT fix, stated plainly: 171px at 1024 and 53px at
          1440 remain, because 825px of table simply does not fit into 656px
          of content. Closing that needs either the global sidebar or S5's
          six-column contract to change, and both are outside this slice.

          Below `xl:` the aside is `display:none`, so it is out of the
          accessibility tree too and the compact select in the toolbar is the
          single folder control a screen reader can reach.

          The track is a flat 240px rather than `minmax(240px,280px)`: 240
          was already the declared minimum, every folder label still sits on
          one line there (measured), and the 40px it returns is 40px the
          table needs more. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="hidden xl:block">
          <DocumentFolderNav
            activeCategory={category}
            onSelect={setCategory}
            total={folderTotal}
            byCategory={folderByCategory}
            countsReady={countsReady}
          />
          {/* Muted, and beside the dashes it explains — never the page's
              own error banner. A summary that failed costs the reader the
              numbers; the list below is untouched and still theirs. */}
          {countsError ? (
            <p className="mt-2 px-1 text-[11px] text-muted-foreground">
              {countsError}
            </p>
          ) : null}
        </aside>

        {/* `min-w-0` for the same reason as `minmax(0,...)` above: without it
            this flex/grid child refuses to shrink below its content. */}
        <div className="min-w-0">
          {/* Stock at a glance, above the controls: the same `category`
              state the rail writes, the same counts the rail reads, and no
              request of its own. It sits inside the list column rather than
              spanning both, so the rail stays the full taxonomy beside a
              grid that shows only the folders holding something. */}
          <DocumentCategoryCards
            categories={CATEGORY_OPTIONS}
            activeCategory={category}
            onSelect={setCategory}
            total={folderTotal}
            byCategory={folderByCategory}
            countsReady={countsReady}
          />

          {filterBar}

          {error ? <p className="mb-4 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
          {initialLoading ? (
            <p className="text-[13px] text-muted-foreground">Lade Dokumente …</p>
          ) : null}

          {empty ? (
            <div className="rounded-[10px] border border-dashed border-border bg-card p-8 text-center">
              <p className="mb-3 text-[13px] font-medium text-muted-foreground">
                {hasFilters
                  ? "Keine Dokumente passen zu den Filtern."
                  : "Noch keine Dokumente. Erstes Dokument hochladen?"}
              </p>
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="rounded-[10px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90"
              >
                + Dokument hochladen
              </button>
            </div>
          ) : null}

          {/* Rendered on `items`, not on `!loading && items`: the rows a reader
              is looking at stay on screen while the next page loads. */}
          {items.length > 0 ? (
            <>
              {/* Mobile: card stack */}
              <div className="block md:hidden space-y-2">
                {items.map((doc) => (
                  <article
                    key={doc.id}
                    className="rounded-[10px] border border-border bg-card p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <DocumentIdentity doc={doc} className="min-w-0 flex-1" />
                      <CategoryPill value={doc.category} />
                    </div>
                    {/* "Zugehörig zu" IS NOW LABELLED, and that is a fix.
                        Before S5 the card printed `related_to_label` as a
                        bare paragraph under the title — "Apt 3.4" with
                        nothing saying what it referred to — while the table
                        gave it a column header. S1 pinned that gap rather
                        than closing it. It is a definition list entry now,
                        like the two facts beside it, and it renders an em
                        dash when empty so the field does not silently
                        vanish on some rows and not others. */}
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                      <div className="col-span-2">
                        <dt className="text-muted-foreground">Zugehörig zu</dt>
                        <dd className="font-medium text-foreground">
                          {doc.related_to_label || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Hochgeladen</dt>
                        <dd className="font-medium tabular-nums text-foreground">
                          {formatDateOnly(doc.uploaded_at)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Größe</dt>
                        <dd className="font-medium tabular-nums text-foreground">
                          {formatBytes(doc.file_size_bytes)}
                        </dd>
                      </div>
                    </dl>
                    <div className="mt-3">
                      <DocumentRowActions
                        doc={doc}
                        busy={busy}
                        onDownload={handleDownload}
                        onDelete={(d) => setPendingDelete(d)}
                      />
                    </div>
                  </article>
                ))}
              </div>

              {/* Tablet/Desktop: table */}
              {/* ONE CARD for the table and the footer beneath it, as the
                  reference has. The scroller keeps its own `overflow-x-auto`
                  — that is where the table's residual width lives (S7) and
                  it must not become the card's overflow, or the rounded
                  corners would clip the scrollbar instead of the table. */}
              <div className="rounded-[12px] border border-border bg-card">
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left text-[13px]">
                  <thead>
                    {/* Still real <th> elements — the table stays a table.
                        The width hint on the first column is what lets the
                        identity column take the slack instead of the four
                        narrow fact columns stretching to fill a wide
                        viewport; the rest stay at their content width. */}
                    <tr className="border-b border-border bg-muted/50 text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
                      <th className="w-[38%] px-4 py-2.5">Dokument</th>
                      <th className="px-4 py-2.5 whitespace-nowrap">Kategorie</th>
                      <th className="px-4 py-2.5 whitespace-nowrap">Zugehörig zu</th>
                      <th className="px-4 py-2.5 whitespace-nowrap">Hochgeladen</th>
                      <th className="px-4 py-2.5 whitespace-nowrap text-right">Größe</th>
                      <th className="px-4 py-2.5 whitespace-nowrap text-right">Aktionen</th>
                    </tr>
                  </thead>
                  {/* More vertical air, a softer hover and no border on the
                      last row: the old `hover:bg-muted` was the full muted
                      tone and read as a selection rather than a pointer. The
                      four fact columns are muted and the identity column is
                      not — that contrast IS the hierarchy, and it is why a
                      reader's eye lands on the title first. */}
                  <tbody>
                    {items.map((doc) => (
                      <tr
                        key={doc.id}
                        className="border-b border-border/60 last:border-0 hover:bg-muted/60"
                      >
                        <td className="px-4 py-3">
                          <DocumentIdentity doc={doc} />
                        </td>
                        {/* `whitespace-nowrap` on the CELL, not on the pill.
                            In this narrow column "Marketing & Kommunikation"
                            wrapped inside its own pill and made that row
                            taller than its neighbours — uneven row heights
                            are exactly what stops a list being scannable. On
                            the card the pill has room and the TITLE is the
                            thing that should wrap, so the shared component
                            stays free to do that there.

                            From `xl:` only, because it costs ~40px and at
                            `lg:` the table is already 165px wider than its
                            container — it has been since before S5. Buying
                            even rows there would be paid for by hiding
                            another column. */}
                        {/* `xl:whitespace-nowrap` REMOVED. S5 added it so a
                            category pill would not wrap on a wide screen, and
                            it was affordable while the longest label was
                            "Marketing & Kommunikation". "Mietverträge &
                            Kündigungen" made it expensive: held on one line it
                            takes the column from 126px to 189px, and those
                            63px come straight out of the Aktionen column at
                            the right edge — measured 81px of internal scroll
                            at 1440 with it, 18px without, against S7's 53px
                            baseline. A pill that wraps to two lines costs a
                            little height; a column pushed behind the
                            scroller costs the reader the action. */}
                        <td className="px-4 py-3">
                          <CategoryPill value={doc.category} />
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {doc.related_to_label || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap tabular-nums text-muted-foreground">
                          {formatDateOnly(doc.uploaded_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-muted-foreground">
                          {formatBytes(doc.file_size_bytes)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DocumentRowActions
                            doc={doc}
                            busy={busy}
                            onDownload={handleDownload}
                            onDelete={(d) => setPendingDelete(d)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

              {/* One footer for both viewports, not one per viewport: the table
                  and the card stack are two renderings of the SAME page, and a
                  second set of page buttons in the DOM would be a second set of
                  "Nächste Seite" controls for one position.

                  The shared component already matches this endpoint's contract
                  (`limit`/`offset`, `total` counted over the same filters) and
                  is already on the theme tokens, so S2 does not regress. No
                  `onLimitChange` is passed, which is how it renders without a
                  rows-per-page selector — S3 ships one fixed page size. */}
                {/* `md:border-t` only: below `md:` the table above is
                    `display:none`, so a top border here would be a line
                    under nothing. */}
                <div className="px-3 py-2 md:border-t md:border-border">
                  <BexioTablePagination
                    total={total}
                    offset={effectiveOffset}
                    limit={PAGE_SIZE}
                    onOffsetChange={setOffset}
                    noun="Dokumente"
                    busy={loading}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        categories={CATEGORY_OPTIONS}
        onUploaded={() => {
          // A document arrived, so both the page and the stock changed.
          // These two are the ONLY counts refetches on this page besides
          // the one on mount — search, sorting, paging and folder clicks
          // all leave the numbers alone because none of them moves stock.
          void load();
          void loadCounts();
        }}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(v) => {
          if (!v) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dokument löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `«${pendingDelete.title}» wird endgültig gelöscht. Die Datei wird auch aus dem Speicher entfernt.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Lösche …" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
